import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { Stage as StageItem } from '../../../features/process/components/stage';
import { Stage } from '../../../app/entities/stage';
import { Project } from '../../../app/entities/project';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { StepItem } from '../../../components/step/step-item';
import { Step } from '../../../app/entities/step';

interface OverViewTabProps {
  project: Project;
}

export const OverviewTab: React.FC<OverViewTabProps> = observer(({ project }) => {
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [startingPosition, setStartingPosition] = useState<number | null>(null);
  const [startingStage, setStartingStage] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const stagesIds = useMemo(() => {
    const ids = project.stages.map((stage) => stage._id);
    return ids;
  }, [project.stages]);

  const onDragStart = (event: DragStartEvent) => {
    if (event.active.data.current?.type === 'step') {
      setActiveStepId(event.active.id.toString());
      const activeStep = Step.getOne(event.active.id.toString());
      if (!activeStep) return;
      setStartingStage(activeStep.stageId);
      setStartingPosition(activeStep.order);
    }
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }
    const activeId = active.data.current?.stepId;
    const overId = over.data.current?.stepId || over.data.current?.stageId;

    if (activeId === overId) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    const isActiveStep = active.data.current?.type === 'step' && activeId;
    const isOverStep = over?.data.current?.type === 'step';
    const isOverStage = over?.data.current?.type === 'stage';

    if (!isActiveStep) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    const activeStep = Step.getOne(activeId);
    if (!activeStep) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    const activeStage = Stage.getOne(activeStep.stageId);
    if (!activeStage) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    if (isOverStep) {
      const overStep = Step.getOne(overId);
      if (!overStep) return;

      const overStage = Stage.getOne(overStep.stageId || overId);
      if (overStage._id !== activeStep.stageId) {
        if (!overStage) return;

        activeStage.removeStep(activeStep._id);
        overStage.insertStep(activeStep, overStep.order || undefined);
        activeStep.stageId = overStage._id;
        overStage.applyReorderSteps();
      }
    } else if (isOverStage) {
      const overStage = Stage.getOne(overId);
      if (!overStage) return;

      activeStage.removeStep(activeStep._id);
      overStage.insertStep(activeStep);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;

    const activeId = active.data.current?.stepId;
    const overId = over.data.current?.stepId || over.data.current?.stageId;

    const isActiveStep = active.data.current?.type === 'step' && activeId;
    const isOverStep = over?.data.current?.type === 'step';

    if (!isActiveStep) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    const activeStep = Step.getOne(activeId);
    if (!activeStep) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    const activeStage = Stage.getOne(activeStep.stageId);

    const overStep = isOverStep ? Step.getOne(overId) : null;
    const overStage = Stage.getOne(overStep?.stageId || overId);

    const order = overStep ? overStep.order : overStage?.steps.length + 1 || 1;

    if (startingStage === activeStep.stageId && startingPosition === order) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    // clear them after we've used them
    setStartingStage(null);
    setStartingPosition(null);

    if (!overStage || !activeStep) {
      setStartingStage(null);
      setStartingPosition(null);
      return;
    }

    if (overStage._id !== activeStep.stageId) {
      activeStep.moveStepToStage(overStage._id || overId, order);
    } else {
      activeStage.updateStepOrder(activeStep._id, order);
    }
  };

  const activeStep = useMemo(() => {
    if (!activeStepId) return null;
    return Step.getOne(activeStepId);
  }, [activeStepId]);

  return (
    <Container>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragOver={(e) => {
          handleDragOver(e);
        }}
        onDragEnd={onDragEnd}
        collisionDetection={pointerWithin}
      >
        <SortableContext items={stagesIds}>
          {project?.stages?.slice().sort((a, b) => a.order - b.order).map((stage: Stage) => (
            <StageItem key={stage._id} stage={stage} />
          ))}
        </SortableContext>

        <DragOverlay>{activeStepId && <StepItem step={activeStep} />}</DragOverlay>
      </DndContext>
    </Container>
  );
});

const Container = styled.div`
  margin-top: 1.6rem;
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  height: 100%;
  padding-bottom: 2.4rem;
  gap: 1.6rem;
`;
